import React, { useState, useEffect } from 'react';
import { Button, Input, InputNumber, Space, Avatar, Divider, Badge, message } from 'antd';
import { 
  DeleteOutlined, 
  ClockCircleOutlined, 
  PlusOutlined,
  SaveOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  CloseOutlined
} from '@ant-design/icons';

const getThumbPath = (item) => {
  if (!item) return '';
  if (item.file_type === 'image') return item.file_url || item.file;
  return `https://escreen.stand.ledscreen.rs/media/${item.thumbnail}`;
};

const MobileCreatePlaylistModal = ({ isVisible, onClose, onCreate, mediaFiles, selectedItems, setSelectedItems, loading, editingPlaylist }) => {
  const [playlistName, setPlaylistName] = useState('');
  const [playlistResolution, setPlaylistResolution] = useState('');

  useEffect(() => {
    if (isVisible) {
      if (editingPlaylist) {
        setPlaylistName(editingPlaylist.name);
        setPlaylistResolution(editingPlaylist.resolution || '');
        const initialItems = (editingPlaylist.items || []).map(item => ({
          ...item.media_details,
          id: `${item.media_details.id}-${Math.random().toString(36).substr(2, 5)}`, 
          duration: item.duration || 5
        }));
        setSelectedItems(initialItems);
      } else {
        setPlaylistName('');
        setPlaylistResolution('');
        setSelectedItems([]);
      }
    }
  }, [isVisible, editingPlaylist, setSelectedItems]);

  const handleCreate = () => {
    if (!playlistName.trim()) {
        message.error('Please enter a playlist name');
        return;
    }
    const itemsForApi = selectedItems.map(item => ({
      id: parseInt(String(item.id).split('-')[0]),
      duration: parseInt(item.duration) || 5
    }));
    onCreate(playlistName, playlistResolution, itemsForApi);
  };

  const addToPlaylist = (media) => {
    const defaultDuration = media.file_type === 'image' ? 5 : (media.duration || 5);
    const newItem = { 
      ...media, 
      id: `${media.id}-${Date.now()}`, 
      duration: defaultDuration 
    };
    setSelectedItems([...selectedItems, newItem]);
    message.success({ content: 'Added to list', duration: 1 });
  };

  const removeItem = (id) => {
    setSelectedItems(selectedItems.filter(i => i.id !== id));
  };

  const updateDuration = (id, dur) => {
    setSelectedItems(selectedItems.map(i => i.id === id ? {...i, duration: dur} : i));
  };

  if (!isVisible) return null;

  return (
    <div className="custom-full-overlay">
      {/* Header */}
      <div className="overlay-header">
        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{editingPlaylist ? 'EDIT PLAYLIST' : 'NEW PLAYLIST'}</span>
        <Button 
          type="text" 
          icon={<CloseOutlined style={{ color: 'white', fontSize: '20px' }} />} 
          onClick={onClose} 
        />
      </div>

      {/* Content - Scrollable */}
      <div className="overlay-body">
        <div style={{ padding: '15px' }}>
          <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
            <label className="field-label">PLAYLIST NAME</label>
            <Input 
              placeholder="Enter name..." 
              value={playlistName} 
              onChange={e => setPlaylistName(e.target.value)} 
              className="custom-input"
            />
            <label className="field-label">RESOLUTION</label>
            <Input 
              placeholder="e.g. 1920x1080" 
              value={playlistResolution} 
              onChange={e => setPlaylistResolution(e.target.value)} 
              className="custom-input"
            />
          </Space>

          <Divider orientation="left" style={{ borderColor: '#334155', color: '#34d399', margin: '10px 0' }}>
            SELECTED <Badge count={selectedItems.length} style={{ backgroundColor: '#3b82f6', marginLeft: '5px' }} />
          </Divider>
          
          <div className="selected-container">
            {selectedItems.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#475569', padding: '20px' }}>No items selected</div>
            ) : (
              selectedItems.map((item, index) => (
                <div key={item.id} className="item-card">
                  <span className="item-index">{index + 1}</span>
                  <Avatar src={getThumbPath(item)} shape="square" size={45} />
                  <div className="item-info">
                    <div className="item-name">{item.name}</div>
                    <div className="item-controls">
                      <ClockCircleOutlined style={{ color: '#facc15', fontSize: '10px' }} />
                      <InputNumber 
                        size="small" 
                        min={1} 
                        value={item.duration} 
                        disabled={item.file_type === 'video'}
                        onChange={v => updateDuration(item.id, v)}
                        className="duration-input"
                      />
                      <span style={{ color: '#64748b', fontSize: '10px' }}>sec.</span>
                    </div>
                  </div>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeItem(item.id)} />
                </div>
              ))
            )}
          </div>

          <Divider orientation="left" style={{ borderColor: '#334155', color: '#3b82f6', margin: '20px 0 10px 0' }}>LIBRARY</Divider>
          <div className="library-list">
            {mediaFiles.map(file => (
              <div key={file.id} className="library-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                  <Avatar src={getThumbPath(file)} shape="square" size={40} icon={file.file_type === 'image' ? <PictureOutlined /> : <VideoCameraOutlined />} />
                  <div className="lib-name">{file.name}</div>
                </div>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => addToPlaylist(file)} style={{ background: '#3b82f6', border: 'none' }}>Add</Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="overlay-footer">
        <Button block onClick={onClose} className="footer-btn-cancel">CANCEL</Button>
        <Button block type="primary" icon={<SaveOutlined />} onClick={handleCreate} loading={loading} className="footer-btn-save">
          SAVE PLAYLIST
        </Button>
      </div>

      <style>{`
        .custom-full-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: #0f172a;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          color: white;
        }

        .overlay-header {
          height: 60px;
          background: #1e293b;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 15px;
          border-bottom: 1px solid #334155;
        }

        .overlay-body {
          flex: 1;
          overflow-y: auto;
          background: #0f172a;
        }

        .overlay-footer {
          padding: 15px;
          background: #1e293b;
          border-top: 1px solid #334155;
          display: flex;
          gap: 10px;
        }

        .field-label { color: #64748b; font-size: 11px; font-weight: bold; }
        
        .custom-input { 
          background: #1e293b !important; 
          color: white !important; 
          border: 1px solid #334155 !important; 
          height: 40px;
        }

        .selected-container { background: #1e293b; border-radius: 8px; padding: 10px; }
        
        .item-card { 
          display: flex; 
          align-items: center; 
          gap: 10px; 
          padding: 8px; 
          background: #0f172a; 
          margin-bottom: 8px; 
          border-radius: 6px;
          border: 1px solid #334155;
        }

        .item-index { color: #64748b; font-size: 10px; width: 15px; }
        .item-info { flex: 1; min-width: 0; }
        .item-name { color: white; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .item-controls { display: flex; align-items: center; gap: 5px; margin-top: 4px; }
        
        .duration-input { 
          width: 55px; 
          background: #1e293b !important; 
          border: none !important; 
          color: white !important;
        }
        .duration-input .ant-input-number-input { color: white !important; }

        .library-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px;
          background: #1e293b;
          margin-bottom: 6px;
          border-radius: 8px;
          border: 1px solid #334155;
        }
        
        .lib-name { color: #94a3b8; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; }

        .footer-btn-cancel { background: #334155 !important; color: white !important; border: none !important; height: 45px; }
        .footer-btn-save { background: #3b82f6 !important; border: none !important; height: 45px; font-weight: bold; }
      `}</style>
    </div>
  );
};

export default MobileCreatePlaylistModal;